import { Injectable } from '@angular/core';
import moment from 'moment';
import { DASHBOARD_FILTERS_STORAGE_KEY } from '@partner/core/const/session-storage-keys';
import {
  DashboardFilters,
  DashboardPeriod,
  DashboardScope,
  DEFAULT_DASHBOARD_FILTERS,
  IsoDate,
} from '@partner/feature-modules/dashboard/domain/model/dashboard-filters.model';
import { companyToday, isValidCustomRange } from '@partner/feature-modules/dashboard/shared/dashboard-filters.helper';
import { DashboardClock } from './dashboard-clock.service';

const ISO_DATE_FORMAT = 'YYYY-MM-DD';

function isEnumMember<T extends Record<string, string>>(enumType: T, value: unknown): value is T[keyof T] {
  return typeof value === 'string' && Object.values(enumType).includes(value);
}

function isIsoDateOrNull(value: unknown): value is IsoDate | null {
  return value === null || (typeof value === 'string' && moment(value, ISO_DATE_FORMAT, true).isValid());
}

/**
 * `sessionStorage` mirror of the dashboard filters. Memory state is authoritative: every access is
 * guarded, so a blocked or unavailable storage degrades to "not persisted" and never throws.
 * `OAuthService.sessionEndLocal` removes the key.
 */
@Injectable({ providedIn: 'root' })
export class DashboardFiltersStorage {
  constructor(private readonly clock: DashboardClock) {}

  /**
   * Validated record, or `null` when nothing usable is stored. A *complete* CUSTOM pair that is no longer valid
   * (`from <= to <= today` violated) is dropped together with the CUSTOM period; a CUSTOM record with a null or
   * half-filled pair is kept as stored and resolves to no range until the user completes it.
   */
  read(): DashboardFilters | null {
    let parsed: unknown;
    try {
      const raw = window.sessionStorage.getItem(DASHBOARD_FILTERS_STORAGE_KEY);
      if (raw === null) {
        return null;
      }
      parsed = JSON.parse(raw);
    } catch {
      return null;
    }
    const filters = this.toFilters(parsed);
    if (!filters) {
      return null;
    }
    if (
      filters.period === DashboardPeriod.CUSTOM &&
      filters.customFrom !== null &&
      filters.customTo !== null &&
      !isValidCustomRange(filters.customFrom, filters.customTo, companyToday(this.clock.now()))
    ) {
      return { ...filters, period: DEFAULT_DASHBOARD_FILTERS.period, customFrom: null, customTo: null };
    }
    return filters;
  }

  write(filters: DashboardFilters): void {
    try {
      window.sessionStorage.setItem(DASHBOARD_FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch {
      // Safari private mode / blocked site data: persistence is best-effort.
    }
  }

  private toFilters(value: unknown): DashboardFilters | null {
    if (typeof value !== 'object' || value === null) {
      return null;
    }
    const candidate = value as Partial<Record<keyof DashboardFilters, unknown>>;
    if (
      !isEnumMember(DashboardPeriod, candidate.period) ||
      !isEnumMember(DashboardScope, candidate.scope) ||
      !isIsoDateOrNull(candidate.customFrom) ||
      !isIsoDateOrNull(candidate.customTo)
    ) {
      return null;
    }
    return {
      period: candidate.period,
      scope: candidate.scope,
      customFrom: candidate.customFrom,
      customTo: candidate.customTo,
    };
  }
}
